/**
 * LGPD — Helpers de anonimização e controle de acesso a dados completos.
 *
 * Estratégia:
 *  - Por padrão, listas e relatórios exibem Nome e CPF anonimizados.
 *  - O operador pode digitar a Senha Técnica do cliente (validada via backend)
 *    para liberar a visualização completa durante a sessão de login.
 *  - O destravamento é guardado em sessionStorage e dura até o logout — não
 *    expira por tempo. O sessionStorage é limpo automaticamente quando o
 *    operador faz logout (ou fecha a aba do navegador).
 */

const LGPD_UNLOCK_KEY = "brave_lgpd_unlock";

export function lgpdAcessoLiberado() {
  try {
    return sessionStorage.getItem(LGPD_UNLOCK_KEY) === "1";
  } catch {
    return false;
  }
}

export function liberarAcessoLgpd() {
  try { sessionStorage.setItem(LGPD_UNLOCK_KEY, "1"); }
  catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent("brave-lgpd-unlock-changed"));
}

export function bloquearAcessoLgpd() {
  try { sessionStorage.removeItem(LGPD_UNLOCK_KEY); }
  catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent("brave-lgpd-unlock-changed"));
}

/**
 * Anonimiza um nome completo preservando o 1º nome inteiro e a inicial
 * de cada nome subsequente seguida de asteriscos.
 *   "João da Silva Santos" → "João da S***** S*****"
 *   "Maria"                → "Maria"
 *   ""                     → ""
 *
 * Mantém conectores curtos (de, da, do, dos, das, e, di, du) em minúsculo.
 */
export function anonNome(nome) {
  if (!nome) return "";
  const n = String(nome).trim();
  if (!n) return "";
  const partes = n.split(/\s+/);
  if (partes.length === 1) return partes[0];
  const conectores = new Set(["de", "da", "do", "dos", "das", "e", "di", "du", "la", "le"]);
  return partes
    .map((p, idx) => {
      if (idx === 0) return p; // 1º nome inteiro
      const baixo = p.toLowerCase();
      if (conectores.has(baixo)) return baixo;
      const visivel = p.slice(0, 1).toUpperCase();
      return visivel + "*".repeat(Math.max(2, p.length - 1));
    })
    .join(" ");
}

/**
 * Anonimiza um CPF preservando apenas os dois primeiros e os dois últimos dígitos.
 *   "12345678901"     → "123.***.***-01"
 *   "123.456.789-01"  → "123.***.***-01"
 *   ""                → ""
 */
export function anonCpf(cpf) {
  if (!cpf) return "";
  const limpo = String(cpf).replace(/\D/g, "");
  if (limpo.length < 4) return cpf;
  if (limpo.length !== 11) {
    // Fallback genérico: mostra 3 primeiros e 2 últimos
    return limpo.slice(0, 3) + ".***.***-" + limpo.slice(-2);
  }
  return `${limpo.slice(0, 3)}.***.***-${limpo.slice(-2)}`;
}

/** Formata CPF completo (quando liberado). */
export function fmtCpf(cpf) {
  if (!cpf) return "";
  const limpo = String(cpf).replace(/\D/g, "");
  if (limpo.length !== 11) return cpf;
  return `${limpo.slice(0, 3)}.${limpo.slice(3, 6)}.${limpo.slice(6, 9)}-${limpo.slice(9)}`;
}

/**
 * Atalho prático para uso direto em renderização — retorna o nome conforme o
 * estado de liberação atual (consulta sessionStorage a cada chamada).
 */
export function nomeOuAnon(nome) {
  return lgpdAcessoLiberado() ? (nome || "") : anonNome(nome);
}

export function cpfOuAnon(cpf) {
  return lgpdAcessoLiberado() ? fmtCpf(cpf) : anonCpf(cpf);
}
